import React, { useRef } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { createChat } from "../../../../actions/chat";
import DropDown, {
  DropDownItem,
  DropDownSubMenu,
  DropDownSubSubMenu,
} from "components/shared/DropDown";
import { isAfter, subMinutes } from "date-fns";
import { toNumber } from "lodash";
import { useTranslationLanguages } from "hooks/useTranslationLanguages";
import { useTranslationUI } from "hooks/useTranslationUI";
import { useTranslationService } from "hooks/useTranslationService";
import { useTranslationConfig } from "hooks/useTranslationConfig";
import { ORIGINAL_LANGUAGE_SIGNIFIER } from "components/shared/TranslationDropDown";
import { useMutation } from "react-query";
import { fetchApi } from "helpers/reactQueryApi";

type MessageSettingsProps = {
  id: string;
  chatId: string;
  fromId: string;
  fromDeleted: boolean;
  myMessage: boolean;
  createdAt: string;
  hasFiles?: boolean;
  className?: string;
  resetTranslation?: (language: string) => void;
  translateObject?: (
    content: any,
    language: string,
    type?: string,
    name?: string,
  ) => void;
};

export default function MessageSettings({
  myMessage,
  className,
  id,
  chatId,
  fromId,
  fromDeleted,
  createdAt,
  resetTranslation,
  hasFiles = false,
  translateObject: onTranslate,
}: MessageSettingsProps) {
  const dispatch = useDispatch();
  const formRef = useRef<HTMLFormElement | null>(null);

  const type = chatId.indexOf("group") === 0 ? "group" : "member";
  const hideStartPrivateChat =
    fromDeleted ||
    myMessage ||
    type === "member" ||
    Preload.current_member?.is_external;

  function setMessageToQuote(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    dispatch({
      type: "chat/COMPOSER/SET_MESSAGE_TO_QUOTE",
      messageId: id,
      chatId,
    });
  }

  function setMessageToEdit(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    dispatch({
      type: "chat/COMPOSER/SET_MESSAGE_TO_EDIT",
      messageId: id,
      chatId,
    });
  }

  function createPrivateChat(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    dispatch(createChat({ payload: { id: fromId, type: "member" } }));
  }

  const { mutate: getToken } = useMutation(
    () =>
      fetchApi("/api/v1/file_token", {
        method: "POST",
        body: { chat_message_id: id },
      }),
    {
      onSuccess: ({ token }) => {
        if (formRef?.current && formRef.current.token) {
          formRef.current.token.value = token;
          formRef.current.submit();
        }
      },
    },
  );

  async function downloadFiles(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    getToken();
  }

  function deleteMessage(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();

    window?.bridge?.confirm(
      I18n.t("js.chat.room.message.dropdown.destroy.confirm"),
      () =>
        dispatch({
          type: "chat/DELETE_MESSAGE",
          payload: {
            id,
          },
        }),
    );
  }

  const canUpdate =
    Preload.current_network.config.chat.message.allow_update &&
    isAfter(
      new Date(createdAt),
      subMinutes(
        new Date(),
        toNumber(
          Preload.current_network.config.chat.message.update_period_of_time,
        ),
      ),
    ) &&
    myMessage;

  const canDestroy =
    Preload.current_network.config.chat.message.allow_destroy &&
    isAfter(
      new Date(createdAt),
      subMinutes(
        new Date(),
        toNumber(
          Preload.current_network.config.chat.message.destroy_period_of_time,
        ),
      ),
    ) &&
    myMessage;

  const { isTranslationEnabled } = useTranslationConfig();
  return (
    <div className={classNames("chatMessageSettings", className)}>
      <DropDown.Toggle className="leading-none px-1" small />
      <DropDown className="dropdown-menu">
        <DropDownItem
          text={I18n.t("js.chat.room.message.quote_message")}
          onSelect={setMessageToQuote}
        />
        {!hideStartPrivateChat && (
          <DropDownItem
            text={I18n.t("js.chat.room.message.start_private_chat")}
            onSelect={createPrivateChat}
          />
        )}
        {canUpdate && (
          <DropDownItem
            text={I18n.t("js.chat.room.message.dropdown.edit.label")}
            onSelect={setMessageToEdit}
          />
        )}
        {canDestroy && (
          <DropDownItem
            text={I18n.t("js.chat.room.message.dropdown.destroy.label")}
            onSelect={deleteMessage}
          />
        )}
        {hasFiles && (
          <>
            <DropDownItem
              text={"Dateien runterladen"}
              onSelect={downloadFiles}
            />
            <form ref={formRef} method="post" action="/api/storage/v2/archive">
              <input type="hidden" name="token" />
            </form>
          </>
        )}
        {isTranslationEnabled && (
          <ChatMessageTranslationDropDownMenu
            messageId={id}
            resetTranslation={resetTranslation}
            onTranslate={onTranslate}
          />
        )}
      </DropDown>
    </div>
  );
}

type ChatMessageTranslationDropDownMenuProps = {
  messageId: string;
  resetTranslation?: (language: string) => void;
  onTranslate?: (
    content: any,
    language: string,
    type?: string,
    name?: string,
  ) => void;
};

function ChatMessageTranslationDropDownMenu({
  messageId,
  onTranslate,
  resetTranslation,
}: ChatMessageTranslationDropDownMenuProps) {
  const {
    primaryLanguages,
    secondaryLanguages,
    selectedLanguage,
    setSelectedLanguage,
  } = useTranslationLanguages();
  const { renderLanguageListItem } = useTranslationUI();
  const { translateObject, setLastUsedLanguage } = useTranslationService();

  function handleLanguageSelect(
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    language: string,
  ) {
    if (!onTranslate || !resetTranslation) return;

    event.preventDefault();
    setLastUsedLanguage(language);
    setSelectedLanguage(language);

    if (language === ORIGINAL_LANGUAGE_SIGNIFIER) {
      resetTranslation(language);
    } else {
      translateObject(messageId, language, "chat_message", onTranslate);
    }
  }

  return (
    <DropDownSubMenu
      text={I18n.t("js.chat.room.message.translate")}
      side="bottom"
    >
      {selectedLanguage !== ORIGINAL_LANGUAGE_SIGNIFIER && (
        <DropDownItem>
          <a
            className="dropdown-item translate-menu-item"
            href="#"
            onClick={(event) =>
              handleLanguageSelect(event, ORIGINAL_LANGUAGE_SIGNIFIER)
            }
          >
            {I18n.t(
              "js.activitystreams.activity.translation_controls.show_original",
            )}
          </a>
        </DropDownItem>
      )}

      {primaryLanguages.map((lang) => (
        <DropDownItem key={lang.language}>
          {renderLanguageListItem(lang, handleLanguageSelect)}
        </DropDownItem>
      ))}

      {secondaryLanguages.length > 0 && (
        <DropDownSubSubMenu
          text={I18n.t("js.activitystreams.activity.translation_controls.more")}
          side="bottom"
        >
          {secondaryLanguages.map((lang) => (
            <DropDownItem key={lang.language}>
              {renderLanguageListItem(lang, handleLanguageSelect)}
            </DropDownItem>
          ))}
        </DropDownSubSubMenu>
      )}
    </DropDownSubMenu>
  );
}
